import React, { useState, useEffect, useCallback } from 'react';
import API_URL from './config';

function AdminBlackouts({ token }) {
  const [blackouts, setBlackouts] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [timezone, setTimezone] = useState('America/New_York');
  const [form, setForm] = useState({
    title: '',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    reason: ''
  });

  const fetchBlackouts = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/blackouts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setBlackouts(Array.isArray(data) ? data : []);
    } catch {
      setBlackouts([]);
    }
  }, [token]);

  useEffect(() => {
    fetchBlackouts();
  }, [fetchBlackouts]);

  const resetForm = () => {
    setForm({ title: '', start_date: '', end_date: '', start_time: '', end_time: '', reason: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    const url = editingId 
      ? `${API_URL}/api/blackouts/${editingId}`
      : `${API_URL}/api/blackouts`;
    const method = editingId ? 'PUT' : 'POST';

    try {
      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save blackout');
      setMessage(editingId ? 'Blackout updated successfully' : 'Blackout created successfully');
      resetForm();
      fetchBlackouts();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (blackout) => {
    setForm({
      title: blackout.title,
      start_date: blackout.start_date.slice(0, 10),
      end_date: blackout.end_date.slice(0, 10),
      start_time: blackout.start_time || '',
      end_time: blackout.end_time || '',
      reason: blackout.reason || ''
    });
    setEditingId(blackout.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blackout?')) return;
    setMessage('');
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/blackouts/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete blackout');
      setMessage('Blackout deleted successfully');
      fetchBlackouts();
    } catch (err) {
      setError(err.message);
    }
  };

  // Fetch timezone setting
  useEffect(() => {
    fetch(`${API_URL}/api/settings/timezone`)
      .then(r => r.json())
      .then(data => { if (data.value) setTimezone(data.value); })
      .catch(() => {});
  }, []);

  const formatDate = (dateStr) => {
    const datePart = dateStr.slice(0, 10);
    const [year, month, day] = datePart.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString('en-US', { timeZone: timezone });
  };

  return (
    <div className="admin-blackouts">
      <div className="section-header">
        <h2>Manage Blackouts</h2>
        <button className="add-btn" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ Add Blackout'}
        </button>
      </div>

      {message && <div className="success">{message}</div>}
      {error && <div className="error">{error}</div>}

      {showForm && (
        <form className="blackout-form" onSubmit={handleSubmit}>
          <h3>{editingId ? 'Edit Blackout' : 'New Blackout'}</h3>
          <div className="form-row">
            <label>
              Title *
              <input
                type="text"
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="e.g., Holiday Closure"
                required
              />
            </label>
          </div>
          <div className="form-row two-col">
            <label>
              Start Date *
              <input
                type="date"
                value={form.start_date}
                onChange={e => setForm({ ...form, start_date: e.target.value })}
                required
              />
            </label>
            <label>
              End Date *
              <input
                type="date"
                value={form.end_date}
                onChange={e => setForm({ ...form, end_date: e.target.value })}
                required
              />
            </label>
          </div>
          <div className="form-row two-col">
            <label>
              Start Time (optional)
              <input
                type="time"
                value={form.start_time}
                onChange={e => setForm({ ...form, start_time: e.target.value })}
              />
              <small>Leave empty for all-day</small>
            </label>
            <label>
              End Time (optional)
              <input
                type="time"
                value={form.end_time}
                onChange={e => setForm({ ...form, end_time: e.target.value })}
              />
            </label>
          </div>
          <div className="form-row">
            <label>
              Reason (optional)
              <textarea
                value={form.reason}
                onChange={e => setForm({ ...form, reason: e.target.value })}
                placeholder="Reason for blackout..."
                rows={2}
              />
            </label>
          </div>
          <div className="form-actions">
            <button type="submit">{editingId ? 'Update' : 'Create'} Blackout</button>
            <button type="button" className="cancel-btn" onClick={resetForm}>Cancel</button>
          </div>
        </form>
      )}

      <div className="blackouts-list">
        {blackouts.length === 0 ? (
          <p className="no-data">No blackouts configured</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Dates</th>
                <th>Times</th>
                <th>Reason</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {blackouts.map(b => (
                <tr key={b.id}>
                  <td><strong>{b.title}</strong></td>
                  <td>
                    {formatDate(b.start_date)}
                    {b.start_date !== b.end_date && ` — ${formatDate(b.end_date)}`}
                  </td>
                  <td>
                    {b.start_time && b.end_time 
                      ? `${b.start_time} - ${b.end_time}`
                      : 'All Day'}
                  </td>
                  <td>{b.reason || '-'}</td>
                  <td className="actions">
                    <button className="edit-btn" onClick={() => handleEdit(b)}>Edit</button>
                    <button className="delete-btn" onClick={() => handleDelete(b.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default AdminBlackouts;
